import React, { useRef } from 'react';
import styled from 'styled-components';
import StyledIcon from './Icon.styled';
import Tooltip from './Tooltip.styled';
import { stripHtmlString } from '../../shared/helpers/General';

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const InputInnerGroup = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const InputLabel = styled.label`
  color: ${({ theme }) => theme.textAreaExpanding.label.fontColor};
  font-family: ${({ theme }) => theme.textAreaExpanding.label.fontFamily};
  font-size: ${({ theme }) => theme.textAreaExpanding.label.fontSize};
  font-weight: 500;
`;

const Instructions = styled.div`
  color: ${({ theme }) => theme.textInput.instructions.fontColor};
  font-family: ${({ theme }) => theme.textInput.instructions.fontFamily};
  font-size: ${({ theme }) => theme.textInput.instructions.fontSize};
  font-weight: ${({ theme }) => theme.textInput.instructions.fontWeight};
  padding-left: 5px;
  overflow-wrap: anywhere;
  p {
    margin-bottom: 0px;
  }
`;

const TextAreaInputControl = styled.textarea`
  border: ${(props) =>
    props.errorMsg && props.errorMsg.length > 0 ? ({ theme }) =>
      theme.textAreaExpanding.error.border : ({ theme }) => theme.textAreaExpanding.border
  };
  box-sizing: border-box;
  color: ${({theme}) => theme.textAreaExpanding.fontColor};
  font-family: ${({theme}) => theme.textAreaExpanding.fontFamily};
  font-size: ${({theme}) => theme.textAreaExpanding.fontSize};
  height: ${props => props.idealHeight || '152px'};
  line-height: ${({theme}) => theme.textAreaExpanding.lineHeight};
  margin-bottom: 0;
  max-height: ${(props) => 
          props.maxHeight && props.maxHeight.length > 0 ? props.maxHeight : ({ theme }) => theme.textAreaExpanding.maxHeight
  };
  min-height: ${({theme}) => theme.textAreaExpanding.height};
  padding: ${({theme}) => theme.textAreaExpanding.padding};
  resize: vertical;
  text-size-adjust: 100%;
  width: 100%;

  &:disabled {
    border: ${({theme}) => theme.textAreaExpanding.disabled.border};
    background-color: ${({theme}) => theme.textAreaExpanding.disabled.backgroundColor};
    color: ${({theme}) => theme.textAreaExpanding.disabled.fontColor};
    cursor: not-allowed;
  }

  &:focus {
    outline-color:  ${({theme}) => theme.textAreaExpanding.focus.outlineColor};
  }

  &::placeholder {
    color: ${({theme}) => theme.textAreaExpanding.placeholder.fontColor};
    font-family: ${({theme}) => theme.textAreaExpanding.placeholder.fontFamily};
    font-size: ${({theme}) => theme.textAreaExpanding.placeholder.fontSize};
    font-style: ${({theme}) => theme.textAreaExpanding.placeholder.fontStyle};
  }
`;

const Error = styled.div`
  color: ${({ theme }) => theme.textAreaExpanding.error.fontColor};
  display: inline-block;
  font-family: ${({ theme }) => theme.textAreaExpanding.error.fontFamily};
  font-size: ${({ theme }) => theme.textAreaExpanding.error.fontSize};
  font-weight: ${({ theme }) => theme.textAreaExpanding.error.fontWeight};
  padding-top: 3px;
  vertical-align: middle;

  svg {
    justify-content: center;
    margin-right: 5px;
    vertical-align: middle;
    display: inline-block;
  }

  span {
    vertical-align: middle;
  }
`;

const Required = styled.span`
  color: ${({ theme }) => theme.textAreaExpanding.error.fontColor};
  padding-left: 5px;
`;

const TooltipContainer = styled.span`
  padding-left: 5px;
`;

const IconDiv = styled.div`
  color: ${({theme}) => theme.textAreaExpanding.disabled.fontColor};
  position: relative;
  right: 28px;
  top: 9px;
  width: 0;
`;

const CharLimit = styled.div`
  color: #555555;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export default function ShortTextAreaInput(props) {
  const idealHeight = useRef(45);
  const lastScrollHeight = useRef(42);
  const textAreaRef = useRef(null);

  if (textAreaRef.current != null && textAreaRef.current != undefined) {
    textAreaRef.current.style.height = '0px';
    let scrollHeight = textAreaRef.current.scrollHeight;

    textAreaRef.current.removeAttribute('style');
    let delta = scrollHeight-lastScrollHeight.current;
    lastScrollHeight.current = scrollHeight;

    // if(scrollHeight > 44){
    //     delta = delta + 50;
    // }

    idealHeight.current = idealHeight.current + delta;
  }

  // Handle instructions display
  let cleanseInstructions = null;
  if (props.instructions && stripHtmlString(props.instructions).trim().length > 0) {
    const updatedIntro = !!props.instructions ? props.instructions.replace(/<p><\/p>/g, '<br />') : '';
    cleanseInstructions = updatedIntro;
  }

  // Handle key down events to prevent Enter key
  const handleKeyDown = (e) => {
    // Prevent Enter key (keyCode 13) from creating new lines
    if (e.keyCode === 13 || e.key === 'Enter') {
      e.preventDefault();
      return false;
    }
    
    // Call the original onKeyDown if it exists
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <InputGroup>
      {props.label && props.label.length > 0 && (
        <InputLabel htmlFor={props.id}>
          {props.label}
          {props.required && <Required>*</Required>}
          {props.tooltip &&
          <TooltipContainer>
            <Tooltip toolTipText={props.tooltip}>
              <StyledIcon type={'InformationCircle'} size={'14px'} />
            </Tooltip>
          </TooltipContainer>}
        </InputLabel>
      )}
      {cleanseInstructions && cleanseInstructions.length > 0 && (
        <Instructions
          dangerouslySetInnerHTML={{
            __html: cleanseInstructions,
          }}
        />
      )}
      <InputInnerGroup>
        <TextAreaInputControl
          aria-invalid={props.errorMsg && props.errorMsg.length > 0 ? 'true' : 'false'}
          aria-label={props.label || 'Text area'}
          disabled={props.disabled || props.readOnly}
          errorMsg={props.errorMsg}
          id={props.id}
          idealHeight={idealHeight.current + "px"}
          maxHeight={props.maxHeight}
          name={props.name}
          placeholder={props.placeholder}
          ref={textAreaRef}
          autoComplete={props.autoComplete}
          maxLength={props.charLimit || ''}
          onKeyUp={props.onKeyUp}
          onKeyDown={handleKeyDown}
          {...props}
        />
        {props.icon && props.icon.length > 0 &&
          <IconDiv>
            <StyledIcon type={props.icon} size={'16px'} />
          </IconDiv>
        }
      </InputInnerGroup>

      <div aria-live="polite" id={`characterCount-${props.id}`}>
        {!!props.charLimit && parseInt(props.charLimit) > 0 && (
          <CharLimit aria-live="polite">{props.charLimit - (parseInt(props.value?.length) || 0)} Characters Remaining</CharLimit>
        )}

        {props.errorMsg && props.errorMsg.length > 0 && (
          <Error>
            <StyledIcon type={'Warning'} size={'16px'} />
            <span role="alert" aria-live="assertive">{props.errorMsg}</span>
          </Error>
        )}
      </div>
    </InputGroup>
  );
} 